import { createAsyncThunk } from "@reduxjs/toolkit";
import { del, get, post, put } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";

/** Normalize GET /system-lists body — arrays may be under list, data, items, records, or nested data.data */
export function extractSystemListArrayFromResponse(raw) {
  if (raw == null) return [];
  const top = raw.data !== undefined ? raw.data : raw;
  if (Array.isArray(top)) return top;
  if (top && typeof top === "object") {
    if (Array.isArray(top.list)) return top.list;
    if (Array.isArray(top.data)) return top.data;
    if (Array.isArray(top.items)) return top.items;
    if (Array.isArray(top.records)) return top.records;
    if (top.data && typeof top.data === "object" && Array.isArray(top.data.data)) {
      return top.data.data;
    }
  }
  return [];
}

/**
 * @param {string | { listType?: string; context?: string; force?: boolean }} arg
 */
export function getSystemListTypeKey(arg) {
  const listType = typeof arg === "string" ? arg : arg?.listType;
  return listType || "JOB_TITLE";
}

/**
 * List system list items by type – GET /system-lists?listType=JOB_TITLE (etc.)
 * Returns array of items (e.g. [{ id, name }, ...] or strings).
 *
 * The client does not skip this request based on `SYSTEM_LIST_ADD` / `EDIT` / `DELETE` — those
 * gate POST/PUT/DELETE in Settings → System lists only. Viewers without those keys still fetch here.
 *
 * Results are stored in `state.systemLists.byListType[listType]`; repeated calls
 * for the same listType return cached data unless `force: true` is passed.
 *
 * Accepts either:
 *  - string listType (backwards compatible), or
 *  - { listType: string, context?: string, force?: boolean } to avoid request cancellation
 *    collisions for the same endpoint across multiple components.
 */
export const fetchSystemList = createAsyncThunk(
  "systemLists/fetch",
  /**
   * @param {string | { listType?: string; context?: string; force?: boolean }} arg
   */
  async (arg, { getState, rejectWithValue }) => {
    const listType = typeof arg === "string" ? arg : arg?.listType;
    const context = typeof arg === "string" ? undefined : arg?.context;
    const force = typeof arg === "object" && arg?.force === true;
    const key = getSystemListTypeKey(arg);

    if (!force) {
      const entry = getState().systemLists?.byListType?.[key];
      if (entry?.status === "succeeded" && Array.isArray(entry.list)) {
        return entry.list;
      }
    }

    try {
      const params = {
        listType: listType || "JOB_TITLE",
      };

      // Add a benign context param to make the request key unique so that
      // ApiBase.get's cancellation logic does not cancel concurrent calls
      // to the same /system-lists endpoint from different components.
      if (context) {
        params._ctx = context;
      }

      const response = await get(API_ENDPOINTS.SYSTEM_LISTS.GET_ALL, params);
      const raw = response.data;
      const list = extractSystemListArrayFromResponse(raw);
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch system list"));
    }
  }
);

/** Slug-style `value` when API expects a separate code from display `name`. */
export function slugValueFromDisplayName(name) {
  const s = String(name ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
  return s || "tag";
}

/**
 * POST /system-lists — body `{ listType, name, value, sortOrder, isActive }`.
 */
export const createSystemListItem = createAsyncThunk(
  "systemLists/createItem",
  async ({ listType, name, value, sortOrder, isActive }, { rejectWithValue }) => {
    try {
      const trimmed = String(name ?? "").trim();
      const resolvedValue =
        value != null && String(value).trim() !== ""
          ? String(value).trim()
          : slugValueFromDisplayName(trimmed);
      const response = await post(
        API_ENDPOINTS.SYSTEM_LISTS.GET_ALL,
        {
          listType,
          name: trimmed,
          value: resolvedValue,
          sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
          isActive: Boolean(isActive),
        },
        {},
        "json",
        { skipToast: true, suppressGlobalErrorModal: true }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to create list item"));
    }
  }
);

/**
 * PUT /system-lists/:id — body `{ name, sortOrder }`.
 */
export const updateSystemListItem = createAsyncThunk(
  "systemLists/updateItem",
  async ({ id, name, sortOrder }, { rejectWithValue }) => {
    try {
      const response = await put(API_ENDPOINTS.SYSTEM_LISTS.BY_ID(String(id)), {
        name: String(name ?? "").trim(),
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update list item"));
    }
  }
);

/**
 * DELETE /system-lists/:id
 */
export const deleteSystemListItem = createAsyncThunk(
  "systemLists/deleteItem",
  async (id, { rejectWithValue }) => {
    try {
      await del(
        API_ENDPOINTS.SYSTEM_LISTS.BY_ID(String(id)),
        {},
        { skipToast: true, suppressGlobalErrorModal: true }
      );
      return String(id);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to delete list item"));
    }
  }
);
